import {
  AfterViewInit,
  Directive,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  Output
} from '@angular/core';
import gsap from "gsap";
import Draggable from "gsap/Draggable";

gsap.registerPlugin(Draggable);

@Directive({
  selector: '[brtDraggableGrid]'
})
export class DraggableGridDirective implements AfterViewInit, OnDestroy {

  draggableElements: HTMLElement[] = [];
  mainHtmlEl!: HTMLElement;

  gap = 12;

  @Input("size") size: number = 0.33;

  itemsPerRow = Math.round(1 / this.size);
  totalRows = 0;

  cellWidth = 0;
  cellHeight = 0;

  isAnimating = false;

  mutationObserver!: MutationObserver;
  isActive = true;
  isDragInProgress = false;

  reInitTimeout = -1;

  @Input("isActive") set setIsActive(value: boolean) {
    this.isActive = value;
  }

  @Input("reInit") set setReInit(value: any) {
    window.clearTimeout(this.reInitTimeout);
    this.reInitTimeout = window.setTimeout(() => {
      this.init();
    }, 50);
  }

  @Output('orderChanged') orderChanged = new EventEmitter<HTMLElement[]>();

  private readonly THRESHOLD = "50%"; // This is amount of overlap between tiles needed to detect a collision

  @HostListener('window:resize', ['$event'])
  onResize($event: Event) {
    this.init();
  }

  constructor(private el: ElementRef) { }

  ngAfterViewInit() {
    if(!this.isActive) return;
    this.mainHtmlEl = this.el.nativeElement as HTMLElement;

    this.mutationObserver = new MutationObserver((mutations, observer) => {
      if(this.draggableElements.length !== this.mainHtmlEl.childElementCount) {

        window.clearTimeout(this.reInitTimeout);
        this.reInitTimeout = window.setTimeout(() => {
          this.init();
        }, 50);
      }

    })

    this.mutationObserver.observe(this.mainHtmlEl, {childList: true});

    this.init();

  }

  init() {
    if(!this.isActive) return;
    this.itemsPerRow = Math.round(1 / this.size);

    this.draggableElements = [];
    const draggableEls = this.mainHtmlEl.childNodes;
    draggableEls.forEach((el) => {
      if(el.nodeType === 1) {
        this.draggableElements.push(el as HTMLElement);
      }
    });

    if(this.draggableElements.length === 0) {
      return;
    }


    const wrapperWidth = this.mainHtmlEl.clientWidth;
    this.totalRows = Math.ceil(this.draggableElements.length / this.itemsPerRow);

    this.cellHeight = this.draggableElements[0].clientHeight;
    this.cellWidth = wrapperWidth * this.size - ((this.itemsPerRow - 1) * this.gap / this.itemsPerRow);
    // console.log(this.cellWidth);

    this.mainHtmlEl.style.height = (this.totalRows * this.cellHeight + this.gap * (this.totalRows - 1)) + "px";
    this.initItems();
  }




  shadow1 = "0 1px 3px  0 rgba(0, 0, 0, 0.5), 0 1px 2px 0 rgba(0, 0, 0, 0.6)";
  shadow2 = "0 6px 10px 0 rgba(0, 0, 0, 0.3), 0 2px 2px 0 rgba(0, 0, 0, 0.2)";

  initItems() {

    this.draggableElements.forEach((el, idx) => {
      el.style.position = "absolute";
      el.style.top = "0";
      el.style.left = "0";
      el.style.width = this.cellWidth + "px";
      el.setAttribute("data-index", idx.toString());

      this.setItemPosition(el, idx, false);
      // console.log(el);
      Draggable.create(el, {
        dragClickables: false,
        trigger: el,
        autoScroll: 2,
        onDrag: (event) => {
          if(!this.isAnimating) {
            this.isDragInProgress = true;
            for(let i = 0; i < this.draggableElements.length; i++) {
              if(this.draggableElements[i] !== el) {
                if(Draggable.hitTest(this.draggableElements[i], el, this.THRESHOLD)) {

                  const currentIndex = parseInt(el.getAttribute("data-index") ?? '0');
                  const targetIndex = parseInt(this.draggableElements[i].getAttribute("data-index") ?? '0');

                  // console.log("New position is", targetIndex);

                  this.reorderItems(currentIndex, targetIndex);

                  this.draggableElements.forEach((localEl, newIndex) => {
                    if(el !== localEl) {
                      // console.log(newIndex);
                      // console.log(localEl);
                      this.setItemPosition(localEl, newIndex, true);
                    }
                  })
                  break;
                }
              }
            }
          }

        },
        cursor: "pointer",
        activeCursor: "grab",
        onPress: () => {
          // console.log("Pressed");
          // el.style.boxShadow = this.shadow1;
        },
        onRelease: () => {
          // el.style.boxShadow = "";

          if(this.isDragInProgress) {
            this.draggableElements.forEach((el, idx) => {
              this.setItemPosition(el, idx, true);
            });



            this.orderChanged.emit(this.draggableElements);
          }

          this.isDragInProgress = false;

        },
        zIndexBoost : true
      });
    })
  }

  reorderItems(fromIndex: number, toIndex: number) {
    const element = this.draggableElements.splice(fromIndex, 1)[0];

    if(fromIndex > toIndex) {

      // Insert it at the new position
      this.draggableElements.splice(toIndex, 0, element);
    } else {
      this.draggableElements.splice(toIndex, 0, element);
    }

    this.draggableElements.forEach((el, idx) => {
      el.setAttribute("data-index", idx.toString());
    })
  }

  setItemPosition(el: HTMLElement, index: number, smooth = true) {
    // console.log(el);
    // console.log(index);
    const row = Math.floor(index / this.itemsPerRow);
    const col = index % this.itemsPerRow;
    // console.log(el);
    // console.log(col * this.cellWidth + col * this.gap + 'px',row * this.cellHeight + row * this.gap + 'px', smooth);
    gsap.to(el,{
      duration: smooth ? 0.1 : 0.0,
      translateX: col * this.cellWidth + col * this.gap + 'px',
      translateY: row * this.cellHeight + row * this.gap + 'px',
      onStart: () => {
        this.isAnimating = true;
      },
      onComplete: () => {
        this.isAnimating = false;
      }
    })
  }


  ngOnDestroy() {
    this.mutationObserver?.disconnect();
  }

}
